import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface AnimatedHeroTextProps {
  isLoaded: boolean;
  className?: string;
  isMobile?: boolean;
}

// Simple text with subtle hover effects
const HoverText = ({ 
  text, 
  className,
  isHighlight = false,
}: { 
  text: string; 
  className?: string;
  isHighlight?: boolean;
}) => {
  return (
    <motion.span
      className={cn(
        "relative inline transition-all duration-300",
        isHighlight && "text-primary",
        isHighlight && "hover:text-glow",
        className
      )}
      whileHover={isHighlight ? { scale: 1.02 } : undefined}
    >
      {/* Subtle glow for highlights */}
      {isHighlight && (
        <span className="absolute inset-0 blur-md bg-primary/10 -z-10 rounded opacity-0 hover:opacity-100 transition-opacity duration-300" />
      )}
      
      <span className={cn("relative", isHighlight && "text-glow")}>
        {text}
      </span>
      
      {/* Underline for highlights */}
      {isHighlight && (
        <span className="absolute -bottom-0.5 left-0 w-full h-[2px] bg-gradient-to-r from-primary to-primary/0" />
      )}
    </motion.span>
  );
};

export const AnimatedHeroText = ({ isLoaded, className, isMobile = false }: AnimatedHeroTextProps) => {
  if (!isLoaded) return null;

  return (
    <motion.div 
      className={cn(
        "relative",
        isMobile ? "mb-3" : "mb-5",
        className
      )}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <h1 className={cn(
        "font-bold tracking-tight leading-tight",
        isMobile ? "text-2xl sm:text-3xl" : "text-4xl lg:text-5xl xl:text-6xl"
      )}>
        {/* First line */}
        <span className="block">
          <HoverText text="Entiende" isHighlight />
          <HoverText text=" los sistemas." className="text-foreground" />
        </span>
        
        {/* Second line */}
        <span className="block mt-0.5 sm:mt-1">
          <HoverText text="Practica con " className="text-foreground" />
          <HoverText text="herramientas" isHighlight />
          <HoverText text=" reales." className="text-foreground" />
        </span>
      </h1>
      
      {/* Decorative code comment */}
      <motion.p
        className={cn(
          "font-mono text-muted-foreground/40",
          isMobile ? "text-[10px] mt-1.5" : "text-xs mt-2"
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.3 }}
      >
        {"// ready_to_learn"}
      </motion.p>
    </motion.div>
  );
};
